import { effect, Injectable, Signal } from '@angular/core';
import {
  cultures,
  Environment,
  EnvironmentService,
  HttpService,
  LocaleService,
  MainState,
  SetLanguage,
  SetTranslations,
  Tenant,
  Translation
} from '@cloud-and-service/portal-lib';
import { Store } from '@ngxs/store';


@Injectable({
  providedIn: 'root',
})
export class TranslationService {
  environment: Environment;
  currentLanguage: Signal<string>;

  translations: Signal<Translation[]>;
  applicationLanguage: Signal<string | null>;
  tenant: Signal<Tenant | null> = this.store.selectSignal(MainState.tenant);

  languageLocaleMapping(culture: string): string {
    return cultures.filter((c) => c.language == culture).map(c => c.isoCode)[0] ?? '';
  }

  constructor(
    private http: HttpService,
    private store: Store,
    private localeService: LocaleService,
    environmentService: EnvironmentService
  ) {
    this.environment = environmentService.getEnvironment();
    this.translations = this.store.selectSignal(MainState.translations);
    this.applicationLanguage = this.store.selectSignal(MainState.language);
    this.currentLanguage = this.localeService.currentLanguageSignal;

    effect(() => {
      const language = this.currentLanguage();
      const tenantId = this.tenant()?.id ?? null;
      // Skip re-fetch if tenant is null (e.g. after logout) and the language
      // hasn't changed — avoids overwriting full tenant translations with common-only ones.
      if (!tenantId && this.translations().length > 0 && this.applicationLanguage() === language) {
        return;
      }
      this.getTranslations(tenantId, language);
    });
  }

  async addTranslation(translation: Translation): Promise<Translation> {
    let url = `${this.environment.apiUrl}/translation/add`;
    return this.http.post<Translation, Translation>(url, translation);
  }

  async updateTranslation(translation: Translation): Promise<Translation> {
    let url = `${this.environment.apiUrl}/translation/update`;
    return this.http.put<Translation, Translation>(url, translation);
  }

  async getTranslationsWithBlanks(tenantId: string | null, language: string | null): Promise<Translation[]> {
    let queryParams = { tenantId: tenantId, culture: language };
    let url = `${this.environment.apiUrl}/translation/list/withBlanks?${this.http.queryGenerator(queryParams)}`;
    return await this.http.get<Translation[]>(url);
  }

  async getTranslations(tenantId?: string | null, language?: string | null): Promise<Translation[]> {
    if (!tenantId) {
      tenantId = this.store.selectSnapshot(MainState.tenant)?.id;
    }
    if (!language) {
      language = this.currentLanguage();
    }

    let queryParams = { tenantId: tenantId, culture: this.languageLocaleMapping(language) };
    let url = `${this.environment.apiUrl}/translation?${this.http.queryGenerator(queryParams)}`;
    return await this.http.get<Translation[]>(url).then((translations: Translation[]) => {
      this.store.dispatch(new SetTranslations(translations));
      this.store.dispatch(new SetLanguage(language));
      return translations;
    });
  }

  async getCurrentTranslations(): Promise<Translation[]> {
    return this.getTranslations();
  }

  async getAvailableLanguages(tenantId?: string | null): Promise<string[]> {
    if (!tenantId) {
      tenantId = this.store.selectSnapshot(MainState.tenant)?.id;
    }

    let queryParams = { tenantId: tenantId };
    let url = `${this.environment.apiUrl}/translation/available-languages?${this.http.queryGenerator(queryParams)}`;
    return await this.http.get<string[]>(url);
  }
}
